import { forwardRef, Ref, SyntheticEvent, useState } from "react"
import { Form, FormikProps } from "formik"
import { Formik } from "../../../../../../../shared/components/forms/SchemaFormik"
import { TabsOrder } from "./formConstants"
import { BookingTimeType, CreateRoomResourceForm, IntegrationType, WalkDirection } from "../../resourcesTypes"
import { RoomFormTabs } from "./RoomFormTabs"
import { TabPanel } from "../../../../../../../shared/components/tabs/tabPanel"
import { FormGeneralContent } from "./general/FormGeneralContent"
import { FormTableArrangementsContent } from "./tableArrangements/FormTableArrangementsContent"
import { AvailabilityContent } from "./availability/AvailabilityContent"
import { AccessControl } from "./accessControl/AccessControl"
import * as yup from "yup"
import { useAppLocalization } from "../../../../../../localization/components/AppLocalizationProvider"
import { useMatchEnvironment } from "../../../../../../environment/envHooks"
import { EnvironmentType } from "../../../../../../environment/envTypes"

const TABLE_ARRANGEMENTS_ENVIRONMENTS = [EnvironmentType.Local, EnvironmentType.Test]

type CreateUpdateRoomFormProps = Readonly<{
    initialValues?: CreateRoomResourceForm | null
    onSubmit: (formData: CreateRoomResourceForm) => void
}>

const defaultInitialValues: CreateRoomResourceForm = {
    reference: "",
    name: "",
    isActive: false,
    image: {
        uid: "",
    },
    description: "",
    email: "",
    buffer: undefined,
    color: "",
    roomDetails: {
        sittings: undefined,
        floor: undefined,
        squareMeters: undefined,
        walkDirection: undefined,
        tableSetups: [],
        isPrivateOffice: false,
        avEquipments: undefined,
    },
    accessControl: {
        limitAccess: false,
        visibleForAll: false,
        departmentIds: [],
    },
    tags: [],
    closingTimes: [],
    bookingTime: undefined,
    bookingTimeType: BookingTimeType.AVAILABLE_ALL_DAY,
    notifications: [],
    closingTimesDaily: undefined,
    isClosingTimes: false,
    isBookingTime: false,
    inService: false,
    integrationType: IntegrationType.NONE,
    integrationEmailAddress: "",
}

type FormikRef = Ref<FormikProps<CreateRoomResourceForm>>

export const CreateUpdateRoomForm = forwardRef(
    ({ initialValues, onSubmit }: CreateUpdateRoomFormProps, ref: FormikRef) => {
        const { l10n } = useAppLocalization()
        const showTableArrangements = useMatchEnvironment(TABLE_ARRANGEMENTS_ENVIRONMENTS)
        const [value, setValue] = useState(TabsOrder.GENERAL)

        const onTabChange = (_event: SyntheticEvent, newValue: number) => {
            setValue(newValue)
        }

        const validationSchema = yup.object({
            reference: yup.string().required(l10n.getString("required-field")),
            name: yup.string().required(l10n.getString("required-field")),
            roomDetails: yup.object({
                sittings: yup.number().min(0).required(l10n.getString("required-field")),
                floor: yup.number().min(0).required(l10n.getString("required-field")),
                squareMeters: yup.number().min(0).required(l10n.getString("required-field")),
                walkDirection: yup.mixed<WalkDirection>().oneOf(Object.values(WalkDirection)),
            }),
        })

        function handleSubmit(values: CreateRoomResourceForm) {
            onSubmit(values)
        }

        return (
            <Formik<CreateRoomResourceForm>
                enableReinitialize
                initialValues={initialValues ? initialValues : defaultInitialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                innerRef={ref}
            >
                <Form>
                    <RoomFormTabs value={value} onChange={onTabChange} />
                    <TabPanel value={value} index={TabsOrder.GENERAL}>
                        <FormGeneralContent />
                    </TabPanel>
                    {showTableArrangements && (
                        <TabPanel value={value} index={TabsOrder.TABLE_ARRANGEMENTS}>
                            <FormTableArrangementsContent />
                        </TabPanel>
                    )}
                    <TabPanel value={value} index={TabsOrder.AVAILABILITY}>
                        <AvailabilityContent />
                    </TabPanel>
                    <TabPanel value={value} index={TabsOrder.ACCESS_CONTROL}>
                        <AccessControl />
                    </TabPanel>
                </Form>
            </Formik>
        )
    },
)
